/**
 * The certified-partner program: tiers, their default discounts, and the rule
 * that decides whether a partner's discount is actually in force.
 *
 * Read by the public Partners page, the staff console (which sets an account's
 * tier and records certifications) and the cloud (which applies the discount
 * in licence quotes). Tier discounts and unit bands are PLACEHOLDERS.
 */

export type PartnerTierId = 'certified' | 'silver' | 'gold' | 'platinum';

export type CredentialType = 'associate' | 'professional';

export interface PartnerTierDef {
  id: PartnerTierId;
  name: string;
  /** Appliance units sold in the trailing 12 months — guidance for staff. */
  minUnits: number;
  maxUnits: number | null;
  /** Default licence discount for the tier; staff may override per account. */
  discountPct: number;
  benefits: string[];
}

export const PARTNER_TIERS: PartnerTierDef[] = [
  {
    id: 'certified',
    name: 'Certified',
    minUnits: 0,
    maxUnits: 9,
    discountPct: 20,
    benefits: ['Partner listing on scada.ink', 'Demo appliance at partner pricing', 'Deal registration'],
  },
  {
    id: 'silver',
    name: 'Silver',
    minUnits: 10,
    maxUnits: 24,
    discountPct: 30,
    benefits: ['Everything in Certified', 'Co-branded sales collateral', 'Priority technical support'],
  },
  {
    id: 'gold',
    name: 'Gold',
    minUnits: 25,
    maxUnits: 49,
    discountPct: 40,
    benefits: ['Everything in Silver', 'Shared leads in your territory', 'Two free exam vouchers a year'],
  },
  {
    id: 'platinum',
    name: 'Platinum',
    minUnits: 50,
    maxUnits: null,
    discountPct: 50,
    benefits: ['Everything in Gold', 'Named partner success manager', 'Early access to new releases', 'Joint roadmap reviews'],
  },
];

export const MAX_PARTNER_DISCOUNT_PCT = 50;

export const CREDENTIALS: Record<CredentialType, { code: string; name: string }> = {
  associate: { code: 'SCA', name: 'Scada.Ink Certified Associate' },
  professional: { code: 'SCP', name: 'Scada.Ink Certified Professional' },
};

/** How long a credential stays current. */
export const CREDENTIAL_VALID_YEARS = 2;

export function findPartnerTier(id: string | null | undefined): PartnerTierDef | undefined {
  return PARTNER_TIERS.find((t) => t.id === id);
}

/** The tier whose unit band contains `units` — a suggestion, never applied automatically. */
export function suggestedTierForUnits(units: number): PartnerTierDef {
  return [...PARTNER_TIERS].reverse().find((t) => units >= t.minUnits) ?? PARTNER_TIERS[0];
}

export interface CredentialRecordLike {
  personEmail: string;
  credential: CredentialType;
  /** ISO date. */
  expiresOn: string;
  revokedAt: string | null;
}

export interface PartnerStanding {
  tier: PartnerTierId | null;
  /** What staff set (tier default or override). */
  configuredDiscountPct: number;
  /** What licence quotes actually use. */
  effectiveDiscountPct: number;
  /** A named person holds a current Associate AND a current Professional credential. */
  qualified: boolean;
  /** Why the effective discount is 0 when a tier is set, for staff and the customer. */
  blockedReason: string | null;
  /** People who hold both current credentials. */
  qualifiedPeople: string[];
}

/**
 * The program rule: a partner discount applies only while at least one person
 * on the account holds BOTH a current Associate and a current Professional
 * credential. An expired or revoked credential pauses the discount without
 * anyone having to remember to change it.
 */
export function partnerStanding(
  tier: PartnerTierId | null,
  discountOverridePct: number | null,
  credentials: CredentialRecordLike[],
  now: Date = new Date(),
): PartnerStanding {
  const today = now.toISOString().slice(0, 10);
  const current = credentials.filter((c) => !c.revokedAt && c.expiresOn >= today);
  const byPerson = new Map<string, Set<CredentialType>>();
  for (const c of current) {
    const key = c.personEmail.trim().toLowerCase();
    if (!byPerson.has(key)) byPerson.set(key, new Set());
    byPerson.get(key)!.add(c.credential);
  }
  const qualifiedPeople = [...byPerson.entries()].filter(([, s]) => s.has('associate') && s.has('professional')).map(([p]) => p);
  const qualified = qualifiedPeople.length > 0;

  const def = findPartnerTier(tier);
  const configured = def ? Math.max(0, Math.min(MAX_PARTNER_DISCOUNT_PCT, discountOverridePct ?? def.discountPct)) : 0;

  let blockedReason: string | null = null;
  if (def && configured > 0 && !qualified) {
    blockedReason = current.length
      ? 'No one on the account holds both a current Associate and a current Professional credential.'
      : 'No current certifications are on record for this account.';
  }

  return {
    tier: def ? def.id : null,
    configuredDiscountPct: configured,
    effectiveDiscountPct: def && qualified ? configured : 0,
    qualified,
    blockedReason,
    qualifiedPeople,
  };
}
